/* =========================================================================
   TrueLight — theme.js
   Vanilla, scoped, PRESENTATION-ONLY JS. No framework, no build step.
   This whole file is PORTABLE — copy it to Shopify assets/theme.js as-is.
   The design-only preview helpers (section include loader + local nav shim)
   live separately in assets/_preview.js, which you simply do not port.
   See CONVENTIONS.md.

   IMPORTANT: There is NO real cart/checkout here. The buy button and qty are
   styled stand-ins inside [data-slot="product-form"]; Shopify's real
   {% form 'product' %} replaces them on conversion.
   ========================================================================= */
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* ---- Product gallery: one scroll-snap track; thumbs + dots scroll to a
     slide; track scroll position drives the active thumb/dot/counter. */
  function initGallery(root) {
    var track = root.querySelector("[data-gallery-track]");
    var slides = root.querySelectorAll("[data-gallery-slide]");
    var thumbs = root.querySelectorAll("[data-gallery-thumb]");
    var dots = root.querySelectorAll("[data-gallery-dot]");
    var counter = root.querySelector("[data-gallery-count]");
    if (!track || !slides.length) return;

    function goto(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
    }
    function setActive(i) {
      thumbs.forEach(function (t, n) { t.setAttribute("aria-current", n === i ? "true" : "false"); });
      dots.forEach(function (d, n) { d.setAttribute("aria-current", n === i ? "true" : "false"); });
      if (counter) counter.textContent = (i + 1) + " / " + slides.length;
    }
    thumbs.forEach(function (t, i) { t.addEventListener("click", function () { goto(i); }); });
    dots.forEach(function (d, i) { d.addEventListener("click", function () { goto(i); }); });
    track.addEventListener("scroll", function () {
      setActive(Math.round(track.scrollLeft / track.clientWidth));
    }, { passive: true });
    setActive(0);

    // Expose so the variant picker can sync the gallery (best-practice #4).
    root._galleryShow = function (src) {
      slides.forEach(function (s, n) {
        var img = s.querySelector("img");
        if (img && img.getAttribute("src") === src) goto(n);
      });
    };
  }

  /* ---- Lightbox zoom (tap/click + pinch is native on the enlarged image) */
  function initLightbox() {
    var box = document.querySelector("[data-lightbox]");
    if (!box) return;
    var bImg = box.querySelector("img");
    var close = box.querySelector("[data-lightbox-close]");
    document.querySelectorAll("[data-zoomable] img").forEach(function (img) {
      img.closest("[data-zoomable]").addEventListener("click", function () {
        bImg.src = img.src; bImg.alt = img.alt;
        box.setAttribute("data-open", "true");
        document.body.style.overflow = "hidden";
      });
    });
    function shut() { box.setAttribute("data-open", "false"); document.body.style.overflow = ""; }
    if (close) close.addEventListener("click", shut);
    box.addEventListener("click", function (e) { if (e.target === box || e.target === bImg) shut(); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") shut(); });
  }

  /* ---- Variant picker (presentation only; updates label, price, gallery) */
  function initVariants(root) {
    // Price/label may sit outside [data-product]; scope to the whole buy box.
    var scope = root.closest(".pdp-buybox") || document;
    var groups = root.querySelectorAll("[data-option-group]");
    groups.forEach(function (group) {
      var label = group.querySelector("[data-option-selected]");
      var btns = group.querySelectorAll("[data-variant]");
      btns.forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (btn.hasAttribute("disabled")) return;
          btns.forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
          btn.setAttribute("aria-pressed", "true");
          if (label) label.textContent = btn.getAttribute("data-variant");

          // Reflect price (best-practice: keep price in sync with variant).
          var price = btn.getAttribute("data-price");
          var was = btn.getAttribute("data-compare");
          var priceEl = scope.querySelector("[data-price-now]");
          var wasEl = scope.querySelector("[data-price-was]");
          if (price && priceEl) priceEl.textContent = price;
          if (was && wasEl) wasEl.textContent = was;

          // Auto-update gallery image for the variant (best-practice #4).
          var img = btn.getAttribute("data-image");
          var gallery = document.querySelector("[data-gallery]");
          if (img && gallery && gallery._galleryShow) gallery._galleryShow(img);

          // Keep the sticky bar price in sync.
          var sbPrice = document.querySelector("[data-stickybar] [data-sb-price]");
          if (price && sbPrice) sbPrice.textContent = price;
        });
      });
    });
  }

  /* ---- Quantity stepper. Wired globally (PDP buy box + cart lines). */
  function initQty(root) {
    root.querySelectorAll("[data-qty]").forEach(function (qty) {
      if (qty._qtyBound) return;
      qty._qtyBound = true;
      var input = qty.querySelector("input");
      if (!input) return;
      var min = parseInt(input.getAttribute("min"), 10);
      if (isNaN(min)) min = 1;
      function set(v) {
        input.value = Math.max(min, v);
        // Notify listeners (the cart line updater listens for this).
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      var dec = qty.querySelector("[data-qty-dec]");
      var inc = qty.querySelector("[data-qty-inc]");
      if (dec) dec.addEventListener("click", function () { set((parseInt(input.value, 10) || min) - 1); });
      if (inc) inc.addEventListener("click", function () { set((parseInt(input.value, 10) || min) + 1); });
    });
  }

  /* ---- Cart line quantity updates (Cart AJAX API, then reload to re-render totals). */
  function initCart() {
    var cart = document.querySelector('[data-section="main-cart"]');
    if (!cart) return;
    var timer;
    cart.addEventListener("change", function (e) {
      var input = e.target.closest && e.target.closest('input[name="updates[]"]');
      if (!input) return;
      var lineEl = input.closest(".cart-line");
      var allLines = Array.prototype.slice.call(cart.querySelectorAll(".cart-line"));
      var line = allLines.indexOf(lineEl) + 1; // Shopify cart lines are 1-based
      if (line < 1) return;
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      cart.setAttribute("aria-busy", "true");
      // Debounce so rapid +/- clicks coalesce into one request + one reload.
      clearTimeout(timer);
      timer = setTimeout(function () {
        var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        fetch(base + "cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ line: line, quantity: qty })
        }).then(function () { window.location.reload(); })
          .catch(function () { window.location.reload(); });
      }, 350);
    });
  }

  /* ---- Accordions (secondary detail only) */
  function initAccordions() {
    document.querySelectorAll("[data-acc-trigger]").forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        var open = trigger.getAttribute("aria-expanded") === "true";
        trigger.setAttribute("aria-expanded", open ? "false" : "true");
        var panel = document.getElementById(trigger.getAttribute("aria-controls"));
        if (panel) panel.setAttribute("data-open", open ? "false" : "true");
      });
    });
  }

  /* ---- Sticky mobile Add-to-Cart: show once inline ATC scrolls away */
  function initStickyBar() {
    var bar = document.querySelector("[data-stickybar]");
    var anchor = document.querySelector("[data-atc-anchor]");
    if (!bar || !anchor || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var show = !e.isIntersecting;
        bar.setAttribute("data-show", show ? "true" : "false");
        bar.setAttribute("aria-hidden", show ? "false" : "true");
        bar.inert = !show;
      });
    }, { rootMargin: "0px 0px -40% 0px" });
    io.observe(anchor);
  }

  /* ---- Mobile nav toggle */
  function initNav() {
    var burger = document.querySelector("[data-nav-toggle]");
    var nav = document.querySelector("[data-mobile-nav]");
    if (burger && nav) burger.addEventListener("click", function () {
      var open = nav.getAttribute("data-open") === "true";
      nav.setAttribute("data-open", open ? "false" : "true");
    });
  }

  /* ---- Hero background parallax (presentation-only, opt-in via [data-parallax]) */
  function initParallax() {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    document.querySelectorAll("[data-parallax]").forEach(function (el) {
      if (el.dataset.parallaxBound) return;
      el.dataset.parallaxBound = "1";
      var hero = el.closest(".hero") || el.parentElement;
      var ticking = false;
      function update() {
        var rect = hero.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          el.style.transform = "translate3d(0," + (window.scrollY * 0.22) + "px,0)";
        }
        ticking = false;
      }
      function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      update();
    });
  }

  /* ---- Email pop-ups (open/close only; app/theme decides WHEN to show) */
  function initPopups() {
    document.querySelectorAll("[data-popup-open]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var p = document.querySelector('[data-popup="' + btn.getAttribute("data-popup-open") + '"]');
        if (p) { p.setAttribute("data-open", "true"); document.body.style.overflow = "hidden"; }
      });
    });
    document.querySelectorAll("[data-popup]").forEach(function (p) {
      function shut() { p.setAttribute("data-open", "false"); document.body.style.overflow = ""; }
      p.querySelectorAll("[data-popup-close]").forEach(function (c) { c.addEventListener("click", shut); });
      document.addEventListener("keydown", function (e) { if (e.key === "Escape") shut(); });
    });
  }

  function boot() {
    document.querySelectorAll("[data-gallery]").forEach(initGallery);
    document.querySelectorAll("[data-product]").forEach(initVariants);
    initQty(document);
    initCart();
    initLightbox();
    initAccordions();
    initStickyBar();
    initNav();
    initParallax();
    initPopups();
  }

  // Bind on load. In Liquid this is all that runs (sections are server-rendered).
  // In local preview, _preview.js re-calls window.TrueLight.boot() once the
  // design-only includes have been fetched in.
  ready(boot);
  window.TrueLight = { boot: boot };
})();
